import React, { ComponentProps, memo, useEffect, useState } from 'react'

import styled from '@emotion/styled'

import { useIsMounted } from '@surglogs-app/hooks'

const DEFAULT_SIZE = 48

interface ThumbnailStyleProps {
  variant: 'square' | 'circle'
  size: number
}

const StyledImage = styled('img', {
  shouldForwardProp: (prop) => !['size', 'variant'].includes(prop),
})<ThumbnailStyleProps>`
  border: 1px solid ${({ theme }) => theme.ui.colors['navy.10']};
  border-radius: ${({ theme, variant, size }) =>
    variant === 'circle' ? `${size}px` : theme.ui.spacings.sm};
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`

const loadImage = (
  src: string,
  fallbackUrl: string,
  maxRetries = 25,
): Promise<string> =>
  new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      resolve(src)
      img.onload = null
    }
    img.onerror = () => {
      // console.error('Thumbnail image load failed', e, img)

      img.onerror = null
      if (maxRetries > 0) {
        resolve(loadImage(src, fallbackUrl, maxRetries - 1))
      } else resolve(fallbackUrl)
    }
    img.src = src
  })

export interface ThumbnailProps
  extends Omit<ComponentProps<typeof StyledImage>, 'src' | 'size' | 'variant'> {
  fallbackUrl: string | undefined
  src: string
  variant?: 'square' | 'circle'
  size?: number
}

// TODO: use Suspense here
const Component = memo(
  ({
    src: originalSrc,
    fallbackUrl,
    variant = 'square',
    size = DEFAULT_SIZE,
    ...rest
  }: ThumbnailProps) => {
    const [src, setSrc] = useState<string>()
    const isMounted = useIsMounted()
    useEffect(() => {
      if (originalSrc && fallbackUrl) {
        loadImage(originalSrc, fallbackUrl).then((newUrl) => {
          if (isMounted()) {
            setSrc(newUrl)
          }
        })
      }
    }, [originalSrc, fallbackUrl, setSrc, isMounted])

    return src ? (
      <StyledImage {...rest} {...{ src, alt: 'thumbnail', variant, size }} />
    ) : null
  },
)

export const Thumbnail = styled(Component)`
  overflow: hidden;
`
